import { CommonModule } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { ProductKeys } from '@config/keys/product.keys';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonAtomComponent } from '@shared/atoms/button-atom/button-atom.component';
import { PriceAtomComponent } from '@shared/atoms/price-atom/price-atom.component';
import { TagAtomComponent } from '@shared/atoms/tag-atom/tag-atom.component';
import { RadialGradientDirective } from '@shared/directives/radial-gradient.directive';
import { VisibleLabelsPipe } from '@shared/pipes/visible-product-labels/visible-product-labels.pipe';
import { hasDiscountPromotionLabel } from '@core/services/eligible-promotions-labels/has-discount-promotion-label.util';
import {
  fromContentBuilderColor,
  isContentBuilderColorConfigured,
} from '@shared/utils/content-builder-color/content-builder-color';
import { KioskProductCardInputs } from '../kiosk-product-card-factory/kiosk-product-cart';

@Component({
  selector: 'app-kiosk-product-card-atom',
  templateUrl: './kiosk-product-card-v1-atom.component.html',
  styleUrls: ['./kiosk-product-card-v1-atom.component.scss'],
  imports: [
    CommonModule,
    ContentBuilderColorPipe,
    ButtonAtomComponent,
    TranslateModule,
    PriceAtomComponent,
    TagAtomComponent,
    VisibleLabelsPipe,
    RadialGradientDirective,
  ],
  standalone: true,
  styles: `
    :host {
      /* Custom elements are inline by default; border on an inline host
         wrapping a block child does not paint around the card. */
      @apply block h-full;
    }
    :host.borderedCard {
      @apply rounded border border-solid p-1;
      > div {
        @apply rounded p-1;
      }
      .unavailable-background {
        @apply rounded;
      }
    }

    :host.shadowCard {
      box-shadow: var(--kiosk-card-shadow);
      @apply p-2;
    }
    :host.roundedCard {
      @apply rounded;
      .unavailable-background {
        @apply rounded-xl;
      }
    }
  `,
  host: {
    // Inline border styles (not a host class): parent `[class]="productCardType()"`
    // would wipe a host class like `has-product-border`.
    '[style.border-color]': '_hostBorderColor()',
    '[style.border-width]': '_hostConfiguredBorderWidth()',
    '[style.border-style]': '_hostConfiguredBorderStyle()',
    '[style.border-radius]': '_hostConfiguredBorderRadius()',
    '[style.padding]': '_hostConfiguredBorderPadding()',
    '[style.background-color]': '_hostBackgroundColor()',
  },
})
export class KioskProductCardV1AtomComponent implements KioskProductCardInputs {
  readonly product = input.required<DoodProductModel>();
  readonly quantity = input.required<number | undefined>();
  readonly isProductAvailable = input<boolean | undefined>(true);
  readonly textColor = input<IContentBuilderFieldColor>();
  readonly cardColor = input<IContentBuilderFieldColor>();
  readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  readonly unavailableButtonBackground = input<IContentBuilderFieldColor>();
  readonly unavailableButtonText = input<IContentBuilderFieldColor>();
  readonly buttonTextColor = input<IContentBuilderFieldColor>();
  readonly priceColor = input<IContentBuilderFieldColor>();
  readonly borderColor = input<IContentBuilderFieldColor>();
  readonly isShowProductIcon = input<boolean>(true);

  protected readonly _hasConfiguredBorderColor = computed(() =>
    isContentBuilderColorConfigured(this.borderColor()),
  );
  protected readonly _hostBorderColor = computed(() =>
    fromContentBuilderColor(this.borderColor(), 'neutral-300'),
  );
  protected readonly _hostConfiguredBorderWidth = computed(() =>
    this._hasConfiguredBorderColor() ? '1px' : null,
  );
  protected readonly _hostConfiguredBorderStyle = computed(() =>
    this._hasConfiguredBorderColor() ? 'solid' : null,
  );
  protected readonly _hostConfiguredBorderRadius = computed(() =>
    this._hasConfiguredBorderColor() ? '0.25rem' : null,
  );
  protected readonly _hostConfiguredBorderPadding = computed(() =>
    this._hasConfiguredBorderColor() ? '0.25rem' : null,
  );
  protected readonly _hostBackgroundColor = computed(() =>
    fromContentBuilderColor(this.cardColor(), 'neutral-50'),
  );

  readonly addProduct = output<void>();
  readonly showProduct = output<void>();

  public get ProductKeys(): typeof ProductKeys {
    return ProductKeys;
  }

  displayDiscount = computed(() => {
    const product = this.product();
    const discount = product[ProductKeys.Discount] ?? 0;
    return discount > 0;
  });

  discountPercentage = computed(() => {
    const product = this.product();
    const originalPrice = product[ProductKeys.Price];
    const discount = product[ProductKeys.Discount] ?? 0;

    if (originalPrice <= 0 || discount <= 0 || originalPrice <= discount) {
      return 0;
    }

    const discountAmount = originalPrice - discount;
    const discountPercentage = Math.round((discountAmount / originalPrice) * 100);
    return discountPercentage;
  });

  addToCart(): void {
    this.addProduct.emit();
  }

  showProductModal(): void {
    this.showProduct.emit();
  }

  protected readonly _hasDiscountPromotionLabel = computed(() =>
    hasDiscountPromotionLabel(this.product()),
  );
}
